import { actorDataExportSchema, type ActorDataExport } from "#actor-data-export-schema";
import { normalizeActorDataExportIdentities } from "#normalize-actor-data-export-identities";
import type { ActorRawDatabaseHandle } from "@-/dev.improve.haniverse.database.actor";
import { actorSchema } from "@-/dev.improve.haniverse.database.schemas.actor.schema";
import { isNonEmpty } from "@-/com.npmjs.ts-array-length";
import arrayLength from "@-/com.npmjs.extra-array__length";

import { getTableColumns } from "@-/com.npmjs.drizzle-orm";
import type { SQLiteTable, SQLiteInsertValue } from "@-/com.npmjs.drizzle-orm/sqlite-core";

type ActorRawTransaction = Parameters<Parameters<ActorRawDatabaseHandle["transaction"]>[0]>[0];

async function insertActorDataRows<TTable extends SQLiteTable>(
  transaction: ActorRawTransaction,
  table: TTable,
  rows: SQLiteInsertValue<TTable>[],
): Promise<void> {
  const chunkSize = Math.max(1, Math.floor(900 / Object.keys(getTableColumns(table)).length));
  for (let offset = 0; offset < rows.length; offset += chunkSize) {
    await transaction.insert(table).values(rows.slice(offset, offset + chunkSize));
  }
}

export function countActorDataExportRows(data: Pick<ActorDataExport, "tables">): number {
  return Object.values(data.tables).reduce((count, rows) => count + arrayLength(rows), 0);
}

/**
 * Replace every actor row with the export's rows, and return how many were written.
 *
 * The rows are first given the ids this app derives for them
 * (`normalizeActorDataExportIdentities`), so an export from any source, including one written
 * before ids were derived, lands in the one form every device agrees on.
 */
export async function replaceActorDataFromExport(
  actorDatabase: ActorRawDatabaseHandle,
  data: ActorDataExport,
): Promise<number> {
  const tables = normalizeActorDataExportIdentities(actorDataExportSchema.parse(data).tables);

  await actorDatabase.transaction(async (transaction) => {
    await transaction.delete(actorSchema.studySession);
    if (isNonEmpty(tables.studySession)) {
      await insertActorDataRows(transaction, actorSchema.studySession, tables.studySession);
    }
    await transaction.delete(actorSchema.rewardClaim);
    if (isNonEmpty(tables.rewardClaim)) {
      await insertActorDataRows(transaction, actorSchema.rewardClaim, tables.rewardClaim);
    }
    await transaction.delete(actorSchema.travelTrip);
    if (isNonEmpty(tables.travelTrip)) {
      await insertActorDataRows(transaction, actorSchema.travelTrip, tables.travelTrip);
    }
    await transaction.delete(actorSchema.userQuest);
    if (isNonEmpty(tables.userQuest)) {
      await insertActorDataRows(transaction, actorSchema.userQuest, tables.userQuest);
    }
    // Delete children before parents so the import remains valid when SQLite
    // foreign-key enforcement is enabled.
    await transaction.delete(actorSchema.userCardReview);
    await transaction.delete(actorSchema.userGraphemeMeaningCard);
    await transaction.delete(actorSchema.userGraphemePronunciationCard);
    await transaction.delete(actorSchema.userWordSentenceCard);
    await transaction.delete(actorSchema.userQuizCard);
    await transaction.delete(actorSchema.collectionMorpheme);
    await transaction.delete(actorSchema.collection);
    await transaction.delete(actorSchema.userCard);
    await transaction.delete(actorSchema.fsrsCardReviewLog);
    await transaction.delete(actorSchema.fsrsCard);
    await transaction.delete(actorSchema.morphemeNote);
    await transaction.delete(actorSchema.morphemeSearchVisit);
    await transaction.delete(actorSchema.userSlide);
    // Standalone: no FK into `user_card` or the static DB, so order is unconstrained.
    await transaction.delete(actorSchema.userSentence);
    await transaction.delete(actorSchema.userWord);
    await transaction.delete(actorSchema.user);

    // Insert parents before their children for the same reason.
    await insertActorDataRows(transaction, actorSchema.user, tables.user);
    if (isNonEmpty(tables.fsrsCard)) {
      await insertActorDataRows(transaction, actorSchema.fsrsCard, tables.fsrsCard);
    }
    if (isNonEmpty(tables.fsrsCardReviewLog)) {
      await insertActorDataRows(
        transaction,
        actorSchema.fsrsCardReviewLog,
        tables.fsrsCardReviewLog,
      );
    }
    if (isNonEmpty(tables.userWord)) {
      await insertActorDataRows(transaction, actorSchema.userWord, tables.userWord);
    }
    if (isNonEmpty(tables.userCard)) {
      await insertActorDataRows(transaction, actorSchema.userCard, tables.userCard);
    }
    if (isNonEmpty(tables.userCardReview)) {
      await insertActorDataRows(transaction, actorSchema.userCardReview, tables.userCardReview);
    }
    if (isNonEmpty(tables.userGraphemeMeaningCard)) {
      await insertActorDataRows(
        transaction,
        actorSchema.userGraphemeMeaningCard,
        tables.userGraphemeMeaningCard,
      );
    }
    if (isNonEmpty(tables.userGraphemePronunciationCard)) {
      await insertActorDataRows(
        transaction,
        actorSchema.userGraphemePronunciationCard,
        tables.userGraphemePronunciationCard,
      );
    }
    if (isNonEmpty(tables.userWordSentenceCard)) {
      await insertActorDataRows(
        transaction,
        actorSchema.userWordSentenceCard,
        tables.userWordSentenceCard,
      );
    }
    if (isNonEmpty(tables.userQuizCard)) {
      await insertActorDataRows(transaction, actorSchema.userQuizCard, tables.userQuizCard);
    }
    if (isNonEmpty(tables.collection)) {
      await insertActorDataRows(transaction, actorSchema.collection, tables.collection);
    }
    if (isNonEmpty(tables.collectionMorpheme)) {
      await insertActorDataRows(
        transaction,
        actorSchema.collectionMorpheme,
        tables.collectionMorpheme,
      );
    }
    if (isNonEmpty(tables.morphemeNote)) {
      await insertActorDataRows(transaction, actorSchema.morphemeNote, tables.morphemeNote);
    }
    if (isNonEmpty(tables.morphemeSearchVisit)) {
      await insertActorDataRows(
        transaction,
        actorSchema.morphemeSearchVisit,
        tables.morphemeSearchVisit,
      );
    }
    if (isNonEmpty(tables.userSlide)) {
      await insertActorDataRows(transaction, actorSchema.userSlide, tables.userSlide);
    }
    if (isNonEmpty(tables.userSentence)) {
      await insertActorDataRows(transaction, actorSchema.userSentence, tables.userSentence);
    }
  });

  return countActorDataExportRows({ tables });
}

export function prepareActorDataReplacement(
  actorDatabase: ActorRawDatabaseHandle,
  data: ActorDataExport,
): { params: unknown[]; sql: string }[] {
  // Same identity rewrite as `replaceActorDataFromExport`.
  const tables = normalizeActorDataExportIdentities(actorDataExportSchema.parse(data).tables);
  const queries: { params: unknown[]; sql: string }[] = [];
  function insert<TTable extends SQLiteTable>(table: TTable, rows: SQLiteInsertValue<TTable>[]) {
    const size = Math.max(1, Math.floor(900 / Object.keys(getTableColumns(table)).length));
    for (let offset = 0; offset < rows.length; offset += size) {
      queries.push(
        actorDatabase
          .insert(table)
          .values(rows.slice(offset, offset + size))
          .toSQL(),
      );
    }
  }
  queries.push(
    actorDatabase.delete(actorSchema.studySession).toSQL(),
    actorDatabase.delete(actorSchema.travelTrip).toSQL(),
    actorDatabase.delete(actorSchema.rewardClaim).toSQL(),
    actorDatabase.delete(actorSchema.userQuest).toSQL(),
    actorDatabase.delete(actorSchema.userCardReview).toSQL(),
    actorDatabase.delete(actorSchema.userGraphemeMeaningCard).toSQL(),
    actorDatabase.delete(actorSchema.userGraphemePronunciationCard).toSQL(),
    actorDatabase.delete(actorSchema.userWordSentenceCard).toSQL(),
    actorDatabase.delete(actorSchema.userQuizCard).toSQL(),
    actorDatabase.delete(actorSchema.collectionMorpheme).toSQL(),
    actorDatabase.delete(actorSchema.collection).toSQL(),
    actorDatabase.delete(actorSchema.userCard).toSQL(),
    actorDatabase.delete(actorSchema.fsrsCardReviewLog).toSQL(),
    actorDatabase.delete(actorSchema.fsrsCard).toSQL(),
    actorDatabase.delete(actorSchema.morphemeNote).toSQL(),
    actorDatabase.delete(actorSchema.morphemeSearchVisit).toSQL(),
    actorDatabase.delete(actorSchema.userSlide).toSQL(),
    actorDatabase.delete(actorSchema.userSentence).toSQL(),
    actorDatabase.delete(actorSchema.userWord).toSQL(),
    actorDatabase.delete(actorSchema.user).toSQL(),
  );
  insert(actorSchema.user, tables.user);
  insert(actorSchema.studySession, tables.studySession);
  insert(actorSchema.travelTrip, tables.travelTrip);
  insert(actorSchema.rewardClaim, tables.rewardClaim);
  insert(actorSchema.userQuest, tables.userQuest);
  insert(actorSchema.fsrsCard, tables.fsrsCard);
  insert(actorSchema.fsrsCardReviewLog, tables.fsrsCardReviewLog);
  insert(actorSchema.userWord, tables.userWord);
  insert(actorSchema.userCard, tables.userCard);
  insert(actorSchema.userCardReview, tables.userCardReview);
  insert(actorSchema.userGraphemeMeaningCard, tables.userGraphemeMeaningCard);
  insert(actorSchema.userGraphemePronunciationCard, tables.userGraphemePronunciationCard);
  insert(actorSchema.userWordSentenceCard, tables.userWordSentenceCard);
  insert(actorSchema.userQuizCard, tables.userQuizCard);
  insert(actorSchema.collection, tables.collection);
  insert(actorSchema.collectionMorpheme, tables.collectionMorpheme);
  insert(actorSchema.morphemeNote, tables.morphemeNote);
  insert(actorSchema.morphemeSearchVisit, tables.morphemeSearchVisit);
  insert(actorSchema.userSlide, tables.userSlide);
  insert(actorSchema.userSentence, tables.userSentence);
  return queries;
}
